<template>
  <div class="prestage-view pb-12">
    <!-- Header -->
    <v-row class="mb-4 align-center px-6 pt-6">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 header-avatar">
          <v-icon color="white" small>fas fa-layer-group</v-icon>
        </v-avatar>
        <div>
          <h1 class="text-h5 font-weight-black primary--text lh-1 mb-1">Gestión de Pre-Producción</h1>
          <p class="caption grey--text text--darken-1 font-weight-medium mb-0">
            Control de diseño, validación y flujos técnicos
          </p>
        </div>
      </div>
      <v-spacer />
      <div class="d-flex align-center header-actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              outlined
              color="primary"
              :loading="loading"
              v-bind="attrs"
              v-on="on"
              class="refresh-btn mr-2"
              @click="refreshAll"
            >
              <v-icon small>fas fa-sync-alt</v-icon>
            </v-btn>
          </template>
          <span>Actualizar datos</span>
        </v-tooltip>
        <export-prestage :disabled="loading" :filter="filter" :options="options" class="mr-2" />
        <export-prestage-products :filter="filter" :options="options" />
      </div>
    </v-row>

    <!-- KPI Summary Cards -->
    <v-row dense class="px-6 mb-4">
      <v-col cols="12" sm="6" md="3" v-for="card in kpiCards" :key="card.key">
        <div
          class="kpi-card pa-4"
          :class="{
            'kpi-card--active': card.filterKey && isKpiActive(card),
            'kpi-card--clickable': !!card.filterKey
          }"
          @click="card.filterKey ? applyKpiFilter(card) : null"
        >
          <div class="d-flex align-center justify-space-between">
            <div class="flex-grow-1 mr-3">
              <div class="d-flex align-center justify-space-between">
                <span class="caption grey--text text--darken-2 font-weight-bold text-uppercase tracking-wider">
                  {{ card.label }}
                </span>
                <span v-if="card.filterKey && isKpiActive(card)" class="kpi-active-badge">
                  Filtrado
                </span>
              </div>
              <div class="text-h4 font-weight-black mt-1" :class="card.colorClass">
                {{ card.value }}
              </div>
            </div>
            <v-avatar :color="card.iconBg" size="42" class="kpi-avatar">
              <v-icon small :color="card.iconColor">{{ card.icon }}</v-icon>
            </v-avatar>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filter & Stage Panel -->
    <div class="filter-panel pa-5 mb-4 mx-6">
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <v-icon x-small color="grey darken-2" class="mr-2">fas fa-filter</v-icon>
          <span class="caption font-weight-bold grey--text text--darken-3 text-uppercase tracking-wider">
            Filtros y Etapas
          </span>
        </div>
        <v-btn
          v-if="hasActiveFilters"
          text
          x-small
          color="error"
          class="font-weight-bold text-none px-2"
          @click="clearFilters"
        >
          <v-icon x-small left>fas fa-times</v-icon>
          Limpiar filtros
        </v-btn>
      </div>

      <!-- Quick filters and dates -->
      <v-row dense class="mb-3 align-center">
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filter.created_after"
            label="Fecha OT desde"
            outlined
            dense
            hide-details="auto"
            type="date"
            clearable
            prepend-inner-icon="fas fa-calendar-alt"
            class="filter-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filter.created_before"
            label="Fecha OT hasta"
            outlined
            dense
            hide-details="auto"
            type="date"
            clearable
            prepend-inner-icon="fas fa-calendar-alt"
            class="filter-input"
            @change="triggerFetch"
          />
        </v-col>
        <v-col cols="6" sm="3" md="3">
          <v-btn
            depressed
            block
            height="40"
            :class="quickUrgent ? 'quick-btn--urgent-active' : 'quick-btn'"
            class="font-weight-bold text-none"
            @click="toggleUrgentFilter"
          >
            <v-icon x-small left :color="quickUrgent ? 'error' : 'grey darken-1'">fas fa-fire</v-icon>
            Urgentes (≤ 3 días)
          </v-btn>
        </v-col>
        <v-col cols="6" sm="3" md="3">
          <v-btn
            depressed
            block
            height="40"
            :class="quickThisMonth ? 'quick-btn--month-active' : 'quick-btn'"
            class="font-weight-bold text-none"
            @click="toggleThisMonthFilter"
          >
            <v-icon x-small left :color="quickThisMonth ? 'primary' : 'grey darken-1'">fas fa-calendar-week</v-icon>
            Este mes
          </v-btn>
        </v-col>
      </v-row>

      <!-- Stage Selector Pills -->
      <div class="pt-2 border-top">
        <div class="caption grey--text text--darken-2 font-weight-bold text-uppercase tracking-wider mb-2">
          Etapa técnica activa
        </div>
        <div class="stage-filter-row">
          <button
            type="button"
            class="stage-pill"
            :class="{ 'stage-pill--active': filter.stage === -2 }"
            @click="setStage(-2)"
          >
            <span class="stage-pill__dot stage-pill__dot--all" />
            <span class="stage-pill__label">Todas las etapas</span>
            <span class="stage-pill__count">{{ summary.total }}</span>
          </button>
          <button
            v-for="stage in stages"
            :key="stage.id"
            type="button"
            class="stage-pill"
            :class="{ 'stage-pill--active': filter.stage === stage.id }"
            :style="stagePillStyle(stage)"
            @click="setStage(stage.id)"
          >
            <span class="stage-pill__dot" :style="{ backgroundColor: stageColor(stage) }" />
            <span class="stage-pill__label">{{ stage.name }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Data Table Container -->
    <div class="table-card mx-6">
      <!-- Batch Action Floating / Sticky Banner -->
      <div v-if="selected.length > 0" class="batch-bar d-flex align-center px-6 py-3">
        <v-avatar size="28" color="primary" class="mr-3">
          <v-icon x-small color="white">fas fa-check</v-icon>
        </v-avatar>
        <span class="body-2 font-weight-bold primary--text mr-4">
          {{ selected.length }} {{ selected.length === 1 ? 'producto seleccionado' : 'productos seleccionados' }}
        </span>
        <v-btn
          color="primary"
          depressed
          small
          class="font-weight-bold text-none rounded-lg mr-2"
          @click.stop="openBatchChange"
        >
          <v-icon x-small class="mr-1">fas fa-exchange-alt</v-icon>
          Cambiar etapa en lote
        </v-btn>
        <v-btn
          text
          small
          color="grey darken-2"
          class="font-weight-bold text-none"
          @click="selected = []"
        >
          Deseleccionar
        </v-btn>
      </div>

      <v-data-table
        v-model="selected"
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        show-select
        item-key="id"
        class="prestage-table"
        :item-class="rowClass"
        @click:row="showProduct"
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 25, 50, 100],
        }"
      >
        <!-- Table Top Toolbar -->
        <template v-slot:top>
          <div class="table-toolbar px-6 py-3">
            <v-text-field
              v-model="search"
              placeholder="Buscar por OT, SKU, producto o cliente..."
              prepend-inner-icon="fas fa-search"
              outlined
              dense
              hide-details="auto"
              clearable
              class="search-input"
              @keyup="searchTimeOut"
              @click:clear="searchTimeOut"
            />
          </div>

          <!-- Active Filter Chips -->
          <div v-if="activeFilterChips.length" class="px-6 pb-3 d-flex flex-wrap gap-2">
            <v-chip
              v-for="chip in activeFilterChips"
              :key="chip.key"
              small
              close
              color="primary"
              outlined
              class="filter-chip font-weight-bold"
              @click:close="chip.clear()"
            >
              {{ chip.label }}
            </v-chip>
          </div>
        </template>

        <!-- Column: OT -->
        <template v-slot:item.work_order__id="{ item }">
          <div
            class="ot-badge"
            title="Ver Orden de Trabajo"
            @click.stop="showWorkOrder({ id: item.work_order__id })"
          >
            #{{ item.work_order__id }}
          </div>
        </template>

        <!-- Column: Referencia / Producto -->
        <template v-slot:item.library_product__sku="{ item }">
          <div class="py-2 product-cell">
            <div class="sku-tag">{{ item.library_product__sku || 'S/SKU' }}</div>
            <div class="product-name font-weight-medium grey--text text--darken-3 mt-1" :title="item.name">
              {{ item.name }}
            </div>
          </div>
        </template>

        <!-- Column: Cliente -->
        <template v-slot:item.work_order__sales_note__customer__name="{ item }">
          <div class="body-2 font-weight-medium client-cell grey--text text--darken-3" :title="item.work_order__sales_note__customer__name">
            <v-icon x-small color="grey lighten-1" class="mr-1">fas fa-building</v-icon>
            {{ item.work_order__sales_note__customer__name || '—' }}
          </div>
        </template>

        <!-- Column: Ingreso -->
        <template v-slot:item.work_order__created_at="{ item }">
          <span class="caption grey--text text--darken-2 font-weight-medium">
            {{ item.work_order__created_at | moment('DD/MM/YYYY') }}
          </span>
        </template>

        <!-- Column: Compromiso -->
        <template v-slot:item.work_order__delivery_date="{ item }">
          <div v-if="item.work_order__delivery_date" class="delivery-badge" :class="`delivery-badge--${deliveryUrgency(item)}`">
            <v-icon x-small left v-if="deliveryUrgency(item) === 'overdue'">fas fa-exclamation-triangle</v-icon>
            <v-icon x-small left v-else-if="deliveryUrgency(item) === 'soon'">fas fa-clock</v-icon>
            <span>{{ item.work_order__delivery_date | moment('DD/MM/YYYY') }}</span>
          </div>
          <span v-else class="caption grey--text">—</span>
        </template>

        <!-- Column: Etapa Actual -->
        <template v-slot:item.prestage="{ item }">
          <v-tooltip bottom max-width="300" open-delay="200">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" class="d-inline-flex">
                <span
                  v-if="item.prestage && item.in_prestage"
                  class="stage-badge"
                  :style="stageBadgeStyle(item.prestage)"
                >
                  <span class="stage-badge__dot" :style="{ backgroundColor: stageColor(item.prestage) }" />
                  <span class="stage-badge__text">{{ item.prestage.name }}</span>
                </span>
                <span
                  v-else
                  class="stage-badge stage-badge--production"
                >
                  <v-icon x-small left color="success">fas fa-industry</v-icon>
                  <span class="stage-badge__text">Producción</span>
                </span>
              </div>
            </template>
            <div v-if="item.pre_stage_logs && item.pre_stage_logs.length" class="py-1">
              <div class="caption font-weight-bold mb-1 d-flex align-center">
                <v-icon x-small color="white" class="mr-1">fas fa-history</v-icon>
                Última trazabilidad
              </div>
              <div class="caption">{{ item.pre_stage_logs[0].email }}</div>
              <div class="caption opacity-80">{{ item.pre_stage_logs[0].date | moment('DD/MM/YYYY HH:mm') }}</div>
            </div>
            <span v-else>Sin historial de etapas registrado</span>
          </v-tooltip>
        </template>

        <!-- Column: Contextual Action Menu ONLY -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-center" @click.stop>
            <v-menu offset-y left transition="slide-y-transition" close-on-content-click>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  class="context-menu-btn"
                  title="Acciones disponibles"
                >
                  <v-icon small color="grey darken-2">fas fa-ellipsis-v</v-icon>
                </v-btn>
              </template>
              <v-list dense class="context-action-list py-1">
                <!-- 1. Ver detalle / trazabilidad -->
                <v-list-item @click="showProduct(item)">
                  <v-list-item-icon class="mr-3 my-1">
                    <v-icon small color="primary">fas fa-eye</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2 font-weight-medium">Ver detalle técnico</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- 2. Cambiar etapa (si está en pre-prensa) -->
                <v-list-item v-if="item.in_prestage" @click="openRowStageChange(item)">
                  <v-list-item-icon class="mr-3 my-1">
                    <v-icon small color="info">fas fa-exchange-alt</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2 font-weight-medium">Cambiar etapa técnica</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- 3. Pasar a producción (si está en pre-prensa) -->
                <v-list-item v-if="item.in_prestage" @click="confirmToProduction(item)">
                  <v-list-item-icon class="mr-3 my-1">
                    <v-icon small color="success">fas fa-industry</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2 font-weight-medium success--text">Pasar a producción</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-divider class="my-1" />

                <!-- 4. Ver Orden de Trabajo -->
                <v-list-item @click="showWorkOrder({ id: item.work_order__id })">
                  <v-list-item-icon class="mr-3 my-1">
                    <v-icon small color="grey darken-2">fas fa-clipboard-list</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2 font-weight-medium">Ver Orden de Trabajo #{{ item.work_order__id }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- 5. Imprimir OT -->
                <v-list-item @click="printWorkOrder(item)">
                  <v-list-item-icon class="mr-3 my-1">
                    <v-icon small color="grey darken-2">fas fa-print</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="body-2 font-weight-medium">Imprimir OT</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="empty-state py-12 text-center">
            <v-avatar size="56" color="grey lighten-4" class="mb-3">
              <v-icon size="26" color="grey darken-1">fas fa-inbox</v-icon>
            </v-avatar>
            <div class="body-1 font-weight-bold grey--text text--darken-3 mb-1">Sin productos encontrados</div>
            <div class="caption grey--text text--darken-1 mb-4">
              No hay productos que coincidan con la búsqueda y filtros aplicados.
            </div>
            <v-btn
              v-if="hasActiveFilters"
              outlined
              small
              color="primary"
              class="font-weight-bold text-none rounded-lg"
              @click="clearFilters"
            >
              <v-icon x-small left>fas fa-times</v-icon>
              Restablecer filtros
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- Modals & Dialogs -->
    <v-dialog v-model="display" max-width="1200" scrollable persistent transition="dialog-bottom-transition">
      <v-card outlined class="modal-surface overflow-hidden elevation-12">
        <work-order-card
          :work_order="workOrderOnDisplay"
          :loading="workOrderOnDisplayLoading"
          @close="display = false"
          @refresh="refreshAll"
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="showChangePrestage" persistent max-width="700" transition="dialog-fade-transition">
      <v-card outlined class="modal-surface overflow-hidden elevation-12">
        <v-sheet color="primary" class="pa-5 d-flex align-center">
          <v-avatar color="primary lighten-1" size="32" class="mr-3">
            <v-icon small color="white">fas fa-sliders-h</v-icon>
          </v-avatar>
          <span class="subtitle-1 white--text font-weight-bold">Ajuste de Flujo Pre-Prensa</span>
          <v-spacer />
          <v-btn icon dark small @click="showChangePrestage = false">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </v-sheet>
        <v-card-text class="pa-0">
          <change-prestage
            :products="selected.length > 0 ? selected : [productToChangePrestage]"
            @close="showChangePrestage = false"
            @updated="onPrestageUpdated"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <detail-prestages-dialog v-model="showProductModal" :product="productOnDisplay" />

    <confirm
      v-if="productToProduction"
      v-model="showProductToProduction"
      :message="`¿Confirma pasar a Producción el producto [${productToProduction.library_product__sku}] de la OT #${productToProduction.work_order__id}? Toda etapa de pre-prensa se dará por finalizada.`"
      @ok="changeToProduction(productToProduction)"
      @cancel="() => { showProductToProduction = false; productToProduction = null; }"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { fetchProductionProducts, getWorkOrder, updateProductionProduct } from '@/api/production'
import notifications from '@/utils/notifications'
import WorkOrderCard from '@/components/production/WorkOrderCard'
import DetailPrestagesDialog from '@/components/production/DetailPrestagesDialog'
import ChangePrestage from '@/components/production/ChangePrestage'
import ExportPrestageProducts from '@/components/production/ExportPrestageProducts'
import ExportPrestage from '@/components/production/ExportPrestage.vue'
import { pdfWorkOrder } from '@/doc_generators/workOrder'

export default {
  name: 'ProductsInPrestage',
  components: {
    WorkOrderCard,
    ExportPrestageProducts,
    ChangePrestage,
    DetailPrestagesDialog,
    ExportPrestage,
  },
  data() {
    return {
      loading: false,
      stages: [],
      filter: {
        created_after: '',
        created_before: '',
        stage: -2,
        delivery_after: '',
        delivery_before: '',
      },
      quickUrgent: false,
      quickThisMonth: false,
      summary: { total: 0, prestage: 0, production: 0, urgent: 0 },
      options: { sortBy: ['work_order__created_at'], sortDesc: [true], page: 1, itemsPerPage: 10 },
      totalItems: 0,
      display: false,
      showChangePrestage: false,
      productToChangePrestage: null,
      workOrderOnDisplay: {},
      workOrderOnDisplayLoading: false,
      search: '',
      items: [],
      selected: [],
      showProductModal: false,
      productOnDisplay: { pre_stage_logs: [] },
      productToProduction: null,
      showProductToProduction: false,
      headers: [
        { text: 'OT', align: 'start', value: 'work_order__id', width: '100' },
        { text: 'Referencia / Producto', align: 'start', value: 'library_product__sku' },
        { text: 'Cliente', value: 'work_order__sales_note__customer__name' },
        { text: 'Ingreso', align: 'start', value: 'work_order__created_at', width: '110' },
        { text: 'Compromiso', align: 'start', value: 'work_order__delivery_date', width: '130' },
        { text: 'Etapa actual', value: 'prestage', align: 'center', sortable: false, width: '160' },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false, width: '60' },
      ],
      timer: null,
    }
  },
  computed: {
    kpiCards() {
      return [
        {
          key: 'total',
          label: 'Total ítems',
          value: this.summary.total,
          icon: 'fas fa-layer-group',
          iconBg: 'grey lighten-4',
          iconColor: 'primary',
          colorClass: 'primary--text',
        },
        {
          key: 'prestage',
          label: 'En pre-producción',
          value: this.summary.prestage,
          icon: 'fas fa-hourglass-half',
          iconBg: 'orange lighten-5',
          iconColor: 'orange darken-2',
          colorClass: 'orange--text text--darken-2',
          filterKey: 'prestage',
        },
        {
          key: 'production',
          label: 'En producción',
          value: this.summary.production,
          icon: 'fas fa-industry',
          iconBg: 'green lighten-5',
          iconColor: 'success',
          colorClass: 'success--text',
          filterKey: 'production',
        },
        {
          key: 'urgent',
          label: 'Urgentes (≤ 3d)',
          value: this.summary.urgent,
          icon: 'fas fa-fire',
          iconBg: 'red lighten-5',
          iconColor: 'error',
          colorClass: 'error--text',
          filterKey: 'urgent',
        },
      ]
    },
    hasActiveFilters() {
      return !!(
        this.search
        || this.filter.created_after
        || this.filter.created_before
        || this.filter.delivery_after
        || this.filter.delivery_before
        || this.filter.stage !== -2
        || this.quickUrgent
        || this.quickThisMonth
      )
    },
    activeFilterChips() {
      const chips = []
      if (this.search) {
        chips.push({ key: 'search', label: `Búsqueda: ${this.search}`, clear: () => { this.search = ''; this.triggerFetch() } })
      }
      if (this.filter.created_after) {
        chips.push({ key: 'ca', label: `Desde ${this.formatDate(this.filter.created_after)}`, clear: () => { this.filter.created_after = ''; this.triggerFetch() } })
      }
      if (this.filter.created_before) {
        chips.push({ key: 'cb', label: `Hasta ${this.formatDate(this.filter.created_before)}`, clear: () => { this.filter.created_before = ''; this.triggerFetch() } })
      }
      if (this.quickUrgent) {
        chips.push({ key: 'urgent', label: 'Urgentes (≤ 3 días)', clear: () => { this.quickUrgent = false; this.applyDeliveryQuickFilters(); this.triggerFetch() } })
      }
      if (this.quickThisMonth) {
        chips.push({ key: 'month', label: 'Este mes', clear: () => { this.quickThisMonth = false; this.applyDeliveryQuickFilters(); this.triggerFetch() } })
      }
      if (this.filter.stage !== -2 && this.filter.stage !== -3) {
        const stage = this.stages.find(s => s.id === this.filter.stage)
        chips.push({
          key: 'stage',
          label: stage ? `Etapa: ${stage.name}` : 'Etapa filtrada',
          clear: () => { this.setStage(-2) },
        })
      } else if (this.filter.stage === -3) {
        chips.push({
          key: 'prestage-only',
          label: 'Solo pre-producción',
          clear: () => { this.setStage(-2) },
        })
      }
      return chips
    },
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format('DD/MM/YYYY') : ''
    },
    stageColor(stage) {
      if (!stage || !stage.color) return '#64748B'
      const map = {
        primary: '#1A1F2E',
        secondary: '#64748B',
        success: '#10B981',
        error: '#EF4444',
        warning: '#F59E0B',
        info: '#35BDC8',
        orange: '#F97316',
        purple: '#8B5CF6',
        blue: '#0284C7',
        red: '#EF4444',
        green: '#10B981',
        yellow: '#EAB308',
      }
      return map[stage.color] || stage.color
    },
    stagePillStyle(stage) {
      if (this.filter.stage !== stage.id) return {}
      const color = this.stageColor(stage)
      return {
        borderColor: color,
        backgroundColor: `${color}14`,
        color: color,
      }
    },
    stageBadgeStyle(stage) {
      const color = this.stageColor(stage)
      return {
        borderColor: `${color}40`,
        backgroundColor: `${color}12`,
        color: color,
      }
    },
    setStage(stageId) {
      this.filter.stage = stageId
      this.triggerFetch()
    },
    isKpiActive(card) {
      if (card.filterKey === 'prestage') return this.filter.stage === -3
      if (card.filterKey === 'production') return this.filter.stage === -1
      if (card.filterKey === 'urgent') return this.quickUrgent
      return false
    },
    applyKpiFilter(card) {
      if (this.isKpiActive(card)) {
        this.clearFilters()
        return
      }
      if (card.filterKey === 'prestage') {
        this.quickUrgent = false
        this.filter.stage = -3
      } else if (card.filterKey === 'production') {
        this.quickUrgent = false
        this.setStage(-1)
        return
      } else if (card.filterKey === 'urgent') {
        this.toggleUrgentFilter()
        return
      }
      this.triggerFetch()
    },
    toggleUrgentFilter() {
      this.quickUrgent = !this.quickUrgent
      if (this.quickUrgent) this.quickThisMonth = false
      this.applyDeliveryQuickFilters()
      this.triggerFetch()
    },
    toggleThisMonthFilter() {
      this.quickThisMonth = !this.quickThisMonth
      if (this.quickThisMonth) this.quickUrgent = false
      this.applyDeliveryQuickFilters()
      this.triggerFetch()
    },
    applyDeliveryQuickFilters() {
      if (this.quickUrgent) {
        this.filter.delivery_after = moment().format('YYYY-MM-DD')
        this.filter.delivery_before = moment().add(3, 'days').format('YYYY-MM-DD')
        return
      }
      if (this.quickThisMonth) {
        this.filter.created_after = moment().startOf('month').format('YYYY-MM-DD')
        this.filter.created_before = moment().endOf('month').format('YYYY-MM-DD')
        this.filter.delivery_after = ''
        this.filter.delivery_before = ''
        return
      }
      this.filter.delivery_after = ''
      this.filter.delivery_before = ''
    },
    clearFilters() {
      this.search = ''
      this.quickUrgent = false
      this.quickThisMonth = false
      this.filter = {
        created_after: '',
        created_before: '',
        stage: -2,
        delivery_after: '',
        delivery_before: '',
      }
      this.triggerFetch()
    },
    buildBaseQuery() {
      const created_after = this.filter.created_after || ''
      const created_before = this.filter.created_before || ''
      const delivery_after = this.filter.delivery_after || ''
      const delivery_before = this.filter.delivery_before || ''
      return `search=${encodeURIComponent(this.search || '')}&created_after=${created_after}&created_before=${created_before}&delivery_after=${delivery_after}&delivery_before=${delivery_before}`
    },
    async loadSummary() {
      const base = this.buildBaseQuery()
      try {
        const today = moment().format('YYYY-MM-DD')
        const urgentEnd = moment().add(3, 'days').format('YYYY-MM-DD')
        const [total, prestage, production, urgent] = await Promise.all([
          fetchProductionProducts(`${base}&page_size=1`),
          fetchProductionProducts(`${base}&in_prestage=true&page_size=1`),
          fetchProductionProducts(`${base}&in_prestage=false&page_size=1`),
          fetchProductionProducts(`${base}&delivery_after=${today}&delivery_before=${urgentEnd}&page_size=1`),
        ])
        this.summary = {
          total: total.count || 0,
          prestage: prestage.count || 0,
          production: production.count || 0,
          urgent: urgent.count || 0,
        }
      } catch (e) {
        // no bloquear la UI por métricas secundarias
      }
    },
    triggerFetch() {
      this.options.page = 1
      this.fetch()
    },
    refreshAll() {
      this.loadSummary()
      this.fetch()
    },
    async fetch() {
      this.loading = true
      let in_prestage = ''
      let stage = this.filter.stage
      if (stage === -2) {
        in_prestage = ''
        stage = ''
      } else if (stage === -3) {
        in_prestage = true
        stage = ''
      } else if (stage === -1) {
        in_prestage = false
        stage = ''
      } else {
        in_prestage = true
      }

      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      const ordering = sortBy.length ? (sortDesc[0] ? '-' : '') + sortBy[0] : '-work_order__created_at'
      const query = `${this.buildBaseQuery()}&ordering=${ordering}&in_prestage=${in_prestage}&page=${page}&page_size=${itemsPerPage}&prestage__id=${stage}`

      try {
        const data = await fetchProductionProducts(query)
        this.totalItems = data.count
        this.items = data.results
        this.loadSummary()
      } catch (err) {
        notifications.showError('Error cargando lista: ' + err)
      } finally {
        this.loading = false
      }
    },
    deliveryUrgency(item) {
      if (!item.work_order__delivery_date) return 'none'
      const delivery = moment(item.work_order__delivery_date).startOf('day')
      const today = moment().startOf('day')
      const diff = delivery.diff(today, 'days')
      if (diff < 0) return 'overdue'
      if (diff <= 3) return 'soon'
      return 'ok'
    },
    rowClass(item) {
      const urgency = this.deliveryUrgency(item)
      if (urgency === 'overdue') return 'row-interactive row-overdue'
      if (urgency === 'soon') return 'row-interactive row-soon'
      return 'row-interactive'
    },
    showProduct(item) {
      this.showProductModal = true
      this.productOnDisplay = item
    },
    async showWorkOrder(item) {
      this.workOrderOnDisplayLoading = true
      this.display = true
      try {
        this.workOrderOnDisplay = await getWorkOrder(item.id)
      } catch (e) {
        notifications.showError('Error al cargar OT: ' + e)
        this.display = false
      } finally {
        this.workOrderOnDisplayLoading = false
      }
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => this.triggerFetch(), 400)
    },
    openBatchChange() {
      this.showChangePrestage = true
    },
    openRowStageChange(item) {
      this.productToChangePrestage = item
      this.selected = []
      this.showChangePrestage = true
    },
    onPrestageUpdated() {
      this.selected = []
      this.showChangePrestage = false
      this.refreshAll()
    },
    confirmToProduction(item) {
      this.productToProduction = item
      this.showProductToProduction = true
    },
    async changeToProduction(item) {
      this.showProductToProduction = false
      this.loading = true
      try {
        await updateProductionProduct(item.id, { in_prestage: false })
        notifications.showSuccess('Producto movido a Producción')
        this.refreshAll()
      } catch (e) {
        notifications.showError('Error: ' + e)
      } finally {
        this.loading = false
        this.productToProduction = null
      }
    },
    printWorkOrder(item) {
      getWorkOrder(item.work_order__id).then(data => {
        pdfWorkOrder(data, this.$store.getters.getUser)
      })
    },
  },
  async mounted() {
    this.stages = [
      ...this.$store.getters.getStages.filter(s => s.enabled !== false),
      { id: -1, name: 'Producción', color: 'success' },
    ]
    await this.fetch()
  },
}
</script>

<style scoped>
.prestage-view {
  max-width: 1800px;
  margin: 0 auto;
  background-color: #f8fafc;
  min-height: 100%;
}

.header-avatar {
  border-radius: 10px;
}

.header-actions {
  gap: 8px;
}

.refresh-btn {
  border-radius: 8px !important;
  background-color: #ffffff;
}

/* KPI Cards */
.kpi-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background-color: #ffffff;
  transition: transform 0.15s ease, border-color 0.15s ease;
  user-select: none;
}

.kpi-card--clickable {
  cursor: pointer;
}

.kpi-card--clickable:hover {
  transform: translateY(-2px);
  border-color: #cbd5e1;
}

.kpi-card--active {
  border-color: var(--v-primary-base) !important;
  background-color: #f1f5f9;
}

.kpi-avatar {
  border-radius: 10px;
}

.kpi-active-badge {
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--v-primary-base);
  background-color: rgba(26, 31, 46, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
}

/* Filter Panel */
.filter-panel {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background-color: #ffffff;
}

.border-top {
  border-top: 1px solid #f1f5f9;
}

.filter-input >>> .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 8px !important;
}

.quick-btn {
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  border-radius: 8px !important;
  border: 1px solid #e2e8f0;
  transition: all 0.15s ease;
}

.quick-btn:hover {
  background-color: #e2e8f0 !important;
}

.quick-btn--urgent-active {
  background-color: #fee2e2 !important;
  color: #b91c1c !important;
  border: 1px solid #fca5a5 !important;
  border-radius: 8px !important;
}

.quick-btn--month-active {
  background-color: #e2e8f0 !important;
  color: var(--v-primary-base) !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
}

/* Stage selector pills */
.stage-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.stage-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  transition: all 0.15s ease;
  cursor: pointer;
}

.stage-pill:hover {
  border-color: #cbd5e1;
  background: #ffffff;
  color: #1e293b;
}

.stage-pill--active {
  border-color: var(--v-primary-base);
  background: #1a1f2e;
  color: #ffffff !important;
}

.stage-pill--active .stage-pill__count {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.stage-pill__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.stage-pill__dot--all {
  background: #64748b;
}

.stage-pill__count {
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e2e8f0;
  font-size: 0.72rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Table card */
.table-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background-color: #ffffff;
  overflow: hidden;
}

.batch-bar {
  background-color: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
}

.table-toolbar {
  border-bottom: 1px solid #f1f5f9;
}

.search-input >>> .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 8px !important;
}

.filter-chip {
  border-radius: 6px !important;
}

/* Table styling */
.prestage-table >>> .v-data-table-header th {
  white-space: nowrap;
  height: 48px !important;
  text-transform: uppercase;
  font-size: 0.7rem !important;
  letter-spacing: 0.8px;
  font-weight: 700 !important;
  background-color: #f8fafc !important;
  color: #475569 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.prestage-table >>> td {
  padding: 10px 14px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.prestage-table >>> tr.row-interactive {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.prestage-table >>> tr.row-interactive:hover {
  background-color: #f8fafc !important;
}

.prestage-table >>> tr.row-soon {
  background-color: #fffbeb !important;
}

.prestage-table >>> tr.row-overdue {
  background-color: #fef2f2 !important;
}

/* Cell elements */
.ot-badge {
  display: inline-block;
  padding: 3px 8px;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: var(--v-primary-base);
  font-size: 0.82rem;
  transition: all 0.15s ease;
}

.ot-badge:hover {
  background-color: var(--v-primary-base);
  color: #ffffff;
  border-color: var(--v-primary-base);
}

.sku-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background-color: #f1f5f9;
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.product-cell .product-name {
  font-size: 0.88rem;
  line-height: 1.35;
  max-width: 380px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.client-cell {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Delivery urgency badges */
.delivery-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.delivery-badge--ok {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.delivery-badge--soon {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.delivery-badge--overdue {
  background-color: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

/* Stage Badge */
.stage-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.stage-badge__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.stage-badge--production {
  border-color: #a7f3d0;
  background-color: #ecfdf5;
  color: #065f46;
}

/* Context Menu Button */
.context-menu-btn {
  border-radius: 8px !important;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  transition: all 0.15s ease;
}

.context-menu-btn:hover {
  background-color: #ffffff;
  border-color: #cbd5e1;
}

.context-action-list {
  min-width: 220px;
  border-radius: 8px !important;
}

/* Modal Surface */
.modal-surface {
  border-radius: 12px !important;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
}

/* Helpers */
.gap-2 { gap: 8px; }
.lh-1 { line-height: 1 !important; }
.tracking-wider { letter-spacing: 0.6px; }
</style>
